import { z, type ZodError, type ZodType, type ZodTypeDef } from "zod"

import { isDefined } from "./Type"

/*
 * Thin wrapper around fetch() that ensures type safety via body validators and explicitly typed error results.
 *
 * Example:
 * request(
    "https://www.anapioficeandfire.com/api/books",
    {
      okType: array(object({ url: string() })),
      signal: controller.signal
    }
  )
 */
export async function request<BODY>(
  url: string | URL,
  options: RequestOptions<BODY>,
): Promise<BODY> {
  const {
    okType,
    signal: fetchSignal,
    headers: fetchHeaders,
    body: fetchBody,
    timeout,
    method = "GET",
    ...fetchOptions
  } = options

  const headers = new Headers(fetchHeaders)

  if (!headers.has("Content-Type") && fetchBody)
    headers.set("Content-Type", "application/json")

  // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
  if (headers.get("Content-Type") === "multipart/form-data")
    headers.delete("Content-Type")

  const token = getBearerToken()
  if (token) headers.set("Authorization", `Bearer ${token}`)

  const body =
    headers.get("Content-Type") === "application/json"
      ? JSON.stringify(fetchBody)
      : (fetchBody as RequestInit["body"])

  let timedOut = false

  try {
    // An artificial timeout on a mutative HTTP method can be dangerous because the side effect
    // might still happen on the server but the client won't know till it's refreshed.
    const canTimeout = Boolean(timeout) && method === "GET"

    const signal = mergeAbortSignals([fetchSignal].filter(isDefined))

    if (canTimeout) {
      setTimeout(() => {
        timedOut = true
      }, timeout)
    }

    const infoBefore = {
      online: navigator.onLine,
      visibilityState: document.visibilityState,
    }

    const pathname = window.location.pathname
    const requestStart = Date.now()

    const response = await fetch(url, {
      ...fetchOptions,
      body,
      headers,
      signal,
      method,
    })

    if (timedOut) {
      const requestDuration = Date.now() - requestStart

      throw new RequestError(url, {
        type: "timeout",
        status: response.status,
        info: {
          ["x-amzn-trace-id"]: response.headers.get("x-amzn-trace-id"),
          ["x-amzn-requestid"]: response.headers.get("x-amzn-requestid"),
          infoBefore,
          infoAfter: {
            online: navigator.onLine,
            visibilityState: document.visibilityState,
          },
          requestDuration: `${requestDuration} ms`,
          pathname,
        },
      })
    }

    const { status } = response
    const textResponse = response.clone()
    const responseBody: unknown = await response
      .json()
      .catch((_) => textResponse.text())

    if (response.ok) {
      const parsed = okType.safeParse(responseBody)

      if (!parsed.success)
        throw new RequestError(url, {
          type: "validation",
          status,
          cause: parsed.error,
        })

      return parsed.data
    } else {
      throw new RequestError(url, { type: "http", status, body: responseBody })
    }
  } catch (error) {
    let mappedError: RequestError

    // If it's our own Error, nothing to do, just reuse as-is.
    if (isRequestError(error)) {
      mappedError = error
    }
    // Recover abort errors with our better error type.
    else if (isAbortError(error)) {
      mappedError = new RequestError(url, { type: "abort" }, error)
    }
    // Unexpected or impossible to catch error (e.g, CORS)
    else {
      mappedError = new RequestError(url, prepareUnknownError(error), error)
    }

    requestErrorHandler(mappedError)
    throw mappedError
  }
}

interface RequestOptions<BODY> extends Omit<RequestInit, "body"> {
  okType: ZodType<BODY, ZodTypeDef, unknown>
  timeout?: number
  body?: RequestInit["body"] | object
}

export class RequestError extends Error {
  constructor(
    url: string | URL,
    public detail: RequestErrorDetail,
    cause?: unknown,
  ) {
    const message = JSON.stringify({ url, ...detail }, null, 2)
    super(message, { cause })
    this.name = "RequestError"
  }
}

function prepareUnknownError(error: unknown): RequestErrorDetail {
  return {
    type: "unknown",
    cause: errorToPlainObject(error),
    info: {
      pathname: window.location.pathname,
      visibilityState: document.visibilityState,
      online: navigator.onLine,
    },
  }
}

// Error instances don't have a toJSON method and none of their properties
// are enumerable so nothing is serialized with JSON.stringify.
// This fixes it.
function errorToPlainObject(error: unknown) {
  if (error instanceof Error)
    return {
      name: error.name,
      message: error.message,
    }

  return error
}

export type RequestErrorDetail =
  | { type: "http"; status: number; body: unknown }
  | { type: "abort" }
  | { type: "timeout"; status: number; info: unknown }
  | {
      type: "unknown"
      cause: unknown
      info: unknown
    }
  | { type: "validation"; status: number; cause: ZodError<unknown> }

export function isRequestError(param: unknown): param is RequestError {
  return param instanceof RequestError
}

export function isAuthError(param: unknown) {
  if (!isRequestError(param)) return false

  if (param.detail.type !== "http") return false

  // Some of our services return 403 when they should really return 401 :(
  // For instance, user-service.prismic.io/profile returns 403 when the session is expired
  // (User is not authorized to access this resource with an explicit deny)
  return param.detail.status === 401 || param.detail.status === 403
}

export const PrismicHttpErrorResponse = z.object({
  message: z.string(),
})
export type PrismicHttpErrorResponse = z.infer<typeof PrismicHttpErrorResponse>

export function isPrismicHttpErrorResponse(
  body: unknown,
): body is PrismicHttpErrorResponse {
  return PrismicHttpErrorResponse.safeParse(body).success
}

function mergeAbortSignals(signals: AbortSignal[]) {
  const controller = new AbortController()

  function onAbort() {
    controller.abort()
    for (const signal of signals) {
      signal.removeEventListener("abort", onAbort)
    }
  }

  for (const signal of signals) {
    if (signal.aborted) {
      onAbort()
      break
    }
    signal.addEventListener("abort", onAbort)
  }

  return controller.signal
}

function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === "AbortError"
}

let getBearerToken: () => string = () => ""
/**
 * Externally registers a way to provide a Bearer token, so this module remains decoupled from App concerns.
 * Also this function could be called conditionally.
 */
export function registerBearerTokenGetter(fn: () => string) {
  getBearerToken = fn
}

let requestErrorHandler: (error: RequestError) => void = () => {}
/**
 * Externally registers a callback to be notified about any request error occurring.
 */
export function registerRequestErrorHandler(fn: (error: RequestError) => void) {
  requestErrorHandler = fn
}

// Makes sure that in dev environments request credentials (cookies) aren't required (we use a header instead)
// Sending the credentials even though we use the Authorization header would lead to a CORS error.
// "include" ensures that credentials are always sent, whether the API is on the same exact origin (sub domains don't count) or not.
// This function is only needed when dealing with APIs hosted on a different origin than Page Builder, since the default of "same-origin" works well otherwise.
export function getRequestCredentials(
  authStrategy: "cookie" | "token",
): RequestInit["credentials"] {
  return authStrategy === "token" ? "omit" : "include"
}
